/**
 * AgentTrail — War Room deliberation event trail component.
 *
 * Displays a collapsible trail of agent tool-calling events, grouped by
 * (agent, round) pairs. Supports grounded value rendering via [CALC:...]
 * tokens and highlights ungrounded dollar assertions for human review.
 *
 * Design Law: docs/plans/2026-07-24-k3-black-orange-design-law.md
 */

import React, { useMemo } from 'react';
import { cn } from '../../lib/utils';

// =============================================================================
// Types
// =============================================================================

export interface DeliberationEvent {
  /** Agent identifier (e.g., 'ai:forensic', 'human:accountant') */
  agent: string;
  /** Round number within the deliberation */
  round: number;
  /** Name of the tool that was called */
  tool_name: string;
  /** Input parameters passed to the tool */
  inputs: Record<string, unknown>;
  /** Summary of the tool result */
  result_summary: string;
  /** Duration of the tool call in milliseconds */
  duration_ms: number;
  /** ISO timestamp of the event */
  ts: string;
}

interface AgentTrailProps {
  events: DeliberationEvent[];
  className?: string;
  /** When provided, engine chips render as clickable buttons that drill into the named engine */
  onEngineDrill?: (engineKey: string) => void;
}

interface GroundedNarrativeProps {
  text: string;
  className?: string;
  /** When provided, engine chips render as clickable buttons that drill into the named engine */
  onEngineDrill?: (engineKey: string) => void;
}

/** Tool-calling safeguard constants (Rule 6) — surfaced for display/reference */
export const MAX_TOOL_ROUNDS = 3;
export const MAX_REANALYSIS_ROUNDS = 2;

// =============================================================================
// Grouping helper
// =============================================================================

type GroupKey = string;
interface EventGroup {
  key: GroupKey;
  agent: string;
  round: number;
  events: DeliberationEvent[];
}

function groupKey(agent: string, round: number): GroupKey {
  return `${agent}::${round}`;
}

function groupEvents(events: DeliberationEvent[]): EventGroup[] {
  const groups = new Map<GroupKey, EventGroup>();

  for (const event of events) {
    const key = groupKey(event.agent, event.round);
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        agent: event.agent,
        round: event.round,
        events: [],
      });
    }
    groups.get(key)!.events.push(event);
  }

  // Preserve original order of first appearance
  const seen = new Set<GroupKey>();
  const result: EventGroup[] = [];
  for (const event of events) {
    const key = groupKey(event.agent, event.round);
    if (!seen.has(key)) {
      seen.add(key);
      result.push(groups.get(key)!);
    }
  }
  return result;
}

// =============================================================================
// findUngroundedDollars — pure function
// =============================================================================

/**
 * Matches dollar amounts in text, excluding those inside [CALC:...] tokens.
 *
 * Patterns supported:
 * - Plain: $1,200, $1.2M, $50k
 * - Parenthesized negatives: ($3,090.63)
 * - Suffixes: K/M/B/k/m/b (case-insensitive)
 */
/**
 * Grounding grammar (conductor-corrected 2026-07-25): a dollar amount in prose
 * is GROUNDED iff it is FOLLOWED — within a short window (<=40 chars, no other
 * dollar or bracket intervening) — by a [CALC:engine_id] tag naming the
 * deterministic engine that produced it, e.g.:
 *   "$155,309.37 [CALC:bottom_up]"            -> grounded by bottom_up
 *   "Deduction of ($3,090.63) applies [CALC:policy_deductions]" -> grounded
 * Any dollar without such a following tag is UNGROUNDED (AI-asserted). The tag
 * carries PROVENANCE (which engine), not the value — this is the on-screen
 * enforcement of "LLMs interpret, code calculates".
 */
const DOLLAR_RE = /\(?\$[\d,]+(?:\.\d+)?[KkMmBb]?\)?/g;
const GROUNDING_TAG_AFTER_RE = /^([^$\[]{0,40})\[CALC:([A-Za-z0-9_.\-]+)\]/;

export interface UngroundedDollar { match: string; index: number }

/**
 * Returns the UNGROUNDED dollar amounts in text as {match, index} pairs —
 * dollars NOT followed by a [CALC:engine_id] grounding tag. Empty array when
 * every dollar is grounded or no dollars exist.
 */
export function findUngroundedDollars(text: string): UngroundedDollar[] {
  if (!text || typeof text !== 'string') return [];
  const out: UngroundedDollar[] = [];
  const re = new RegExp(DOLLAR_RE.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const after = text.slice(m.index + m[0].length);
    if (!GROUNDING_TAG_AFTER_RE.test(after)) {
      out.push({ match: m[0], index: m.index });
    }
  }
  return out;
}

// =============================================================================
// GroundedNarrative component
// =============================================================================

/**
 * Parses text into segments:
 * - $X [CALC:engine_id] → grounded dollar + engine provenance chip
 * - bare [CALC:engine_id] → engine chip
 * - Bare ungrounded dollar amounts → render with warning badge (amber per Standing Law)
 * - Everything else → plain text
 *
 * Standing Law convention for ungrounded dollars (per VerifiedValue/FinancialValue):
 * - Orange/amber warning badge with 'AI' label
 * - title="AI-asserted — no engine source"
 */
export function GroundedNarrative({ text, className, onEngineDrill }: GroundedNarrativeProps) {
  const segments = useMemo(() => {
    if (!text) return [];
    type Seg =
      | { type: 'text'; content: string }
      | { type: 'grounded'; value: string; between: string; engine: string }
      | { type: 'chip'; engine: string }
      | { type: 'ungrounded'; value: string };
    const result: Seg[] = [];
    const DOLLAR_OR_TAG = /(\(?\$[\d,]+(?:\.\d+)?[KkMmBb]?\)?)|(\[CALC:([A-Za-z0-9_.\-]+)\])/g;
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = DOLLAR_OR_TAG.exec(text)) !== null) {
      if (m.index > last) result.push({ type: 'text', content: text.slice(last, m.index) });
      if (m[1]) {
        const rest = text.slice(m.index + m[1].length);
        const g = rest.match(GROUNDING_TAG_AFTER_RE);
        if (g) {
          result.push({ type: 'grounded', value: m[1], between: g[1], engine: g[2] });
          DOLLAR_OR_TAG.lastIndex = m.index + m[1].length + g[0].length;
        } else {
          result.push({ type: 'ungrounded', value: m[1] });
        }
      } else {
        result.push({ type: 'chip', engine: m[3] });
      }
      last = DOLLAR_OR_TAG.lastIndex;
    }
    if (last < text.length) result.push({ type: 'text', content: text.slice(last) });
    return result;
  }, [text]);

  const engineChip = (engine: string, key: number) => {
    const chipClassName = 'inline-flex items-center gap-1 align-middle rounded px-1 py-px font-mono text-[10px]';
    const chipStyle = {
      color: 'var(--color-verified)',
      backgroundColor: 'var(--color-bg-elevated)',
      border: '1px solid var(--color-hairline)',
    };
    const chipTitle = `Computed by engine: ${engine}`;

    if (onEngineDrill) {
      return (
        <button
          key={key}
          type="button"
          className={cn(chipClassName, 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-[var(--color-brand)]')}
          style={chipStyle}
          title={chipTitle}
          aria-label={`Drill into engine: ${engine}`}
          onClick={() => onEngineDrill(engine)}
        >
          {engine}
        </button>
      );
    }

    return (
      <span key={key} className={chipClassName} style={chipStyle} title={chipTitle}>
        {engine}
      </span>
    );
  };

  // Amber warning badge for ungrounded dollars (Standing Law VerifiedValue convention)
  const ungroundedBadge = (value: string, key: number) => (
    <span key={key} className="inline-flex items-center gap-1 align-middle">
      <span
        className="font-mono tabular-nums"
        title="AI-asserted — no engine source"
      >
        {value}
      </span>
      <span
        className="inline-flex items-center justify-center rounded-sm text-[10px] font-semibold uppercase leading-none"
        style={{
          width: '16px',
          height: '16px',
          backgroundColor: 'var(--color-needs-human)',
          color: 'var(--color-bg-base)',
        }}
        title="AI-asserted — no engine source"
      >
        AI
      </span>
    </span>
  );

  return (
    <span className={cn('break-words', className)}>
      {segments.map((seg, idx) => {
        if (seg.type === 'text') return <span key={idx}>{seg.content}</span>;
        if (seg.type === 'grounded') {
          return (
            <span key={idx} className="inline-flex items-center gap-1 align-middle">
              <span className="font-mono tabular-nums" title={`Grounded by ${seg.engine}`}>
                {seg.value}
              </span>
              {seg.between}
              {engineChip(seg.engine, idx * 1000 + 1)}
            </span>
          );
        }
        if (seg.type === 'chip') return engineChip(seg.engine, idx);
        return ungroundedBadge(seg.value, idx);
      })}
    </span>
  );
}

// =============================================================================
// AgentTrail component
// =============================================================================

// Inline SVG icons (no icon libraries per constraints)
const ChevronDownIcon = ({ className }: { className?: string }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ClockIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const ToolIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
);

/**
 * Formats agent name for display per Rule 5:
 * - ai:forensic → "ai:forensic"
 * - human:accountant → "human:accountant"
 * Bare names without prefix are displayed as-is (shouldn't happen per spec)
 */
function formatAgentName(agent: string): string {
  return agent;
}

/**
 * Formats duration in milliseconds to human-readable string
 */
function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/**
 * Renders a single event group row with collapsible inputs
 */
function EventGroupRow({
  group,
  onEngineDrill,
}: {
  group: EventGroup;
  onEngineDrill?: (engineKey: string) => void;
}) {
  const firstEvent = group.events[0];
  const totalDuration = group.events.reduce((sum, e) => sum + e.duration_ms, 0);

  // All events in group share agent/round; tool_name from first event
  const toolName = firstEvent?.tool_name ?? 'unknown';

  return (
    <details className="group rounded border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      <summary className="flex cursor-pointer list-none items-center gap-3 px-3 py-2 hover:bg-[var(--color-bg-hover)]">
        {/* Expand/collapse chevron */}
        <ChevronDownIcon className="shrink-0 text-[var(--color-text-dim)] transition-transform group-open:rotate-180" />

        {/* Agent name with prefix per Rule 5 */}
        <span className="shrink-0 font-mono text-xs text-[var(--color-text-secondary)]">
          {formatAgentName(group.agent)}
        </span>

        {/* Round badge */}
        <span className="shrink-0 rounded-full bg-[var(--color-bg-elevated)] px-2 py-0.5 text-[10px] text-[var(--color-text-muted)]">
          R{group.round}
        </span>

        {/* Tool name */}
        <span className="flex flex-1 items-center gap-1.5 text-xs text-[var(--color-text-secondary)]">
          <span className="text-[var(--color-text-dim)]">
            <ToolIcon />
          </span>
          {toolName}
        </span>

        {/* Duration — suppressed entirely when the group's total is 0 (Res 3, K3 P0):
            all durations on the live trail are DERIVED-ZERO (no per-event timing exists
            on the backend timeline wire shape), so a rendered "0ms" would be fabricated
            precision. Only render the chip when there is a real, non-zero duration. */}
        {totalDuration > 0 && (
          <span className="flex shrink-0 items-center gap-1 text-[11px] text-[var(--color-text-dim)]">
            <ClockIcon />
            {formatDuration(totalDuration)}
          </span>
        )}
      </summary>

      {/* Collapsed content: inputs for each event in the group */}
      <div className="border-t border-[var(--color-border-sub)] px-3 py-2">
        {group.events.map((event, idx) => (
          <div
            key={idx}
            className={cn(
              'mb-2 last:mb-0',
              idx > 0 && 'mt-2 border-t border-[var(--color-border-faint)] pt-2'
            )}
          >
            {/* Event metadata — per-event duration is suppressed at 0 for the same
                derived-zero reason as the group chip above (Res 3): no per-event
                timing exists on the live-trail wire shape, so "0ms" here would be
                the same fabricated precision, just one level deeper. */}
            <div className="mb-1 flex items-center gap-2 text-[11px] text-[var(--color-text-dim)]">
              <span>{new Date(event.ts).toLocaleTimeString()}</span>
              {event.duration_ms > 0 && (
                <>
                  <span>·</span>
                  <span>{formatDuration(event.duration_ms)}</span>
                </>
              )}
            </div>

            {/* Inputs as collapsible JSON */}
            <details className="rounded bg-[var(--color-bg-elevated)]">
              <summary className="cursor-pointer px-2 py-1 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]">
                inputs
              </summary>
              <pre className="m-0 overflow-auto p-2 text-[11px] text-[var(--color-text-muted)]">
                {JSON.stringify(event.inputs, null, 2)}
              </pre>
            </details>

            {/* Result summary — wired through GroundedNarrative (no truncation) */}
            {event.result_summary && (
              <div className="mt-1 text-[11px] text-[var(--color-text-dim)]">
                <span className="text-[var(--color-text-muted)]">→ </span>
                <GroundedNarrative text={event.result_summary} onEngineDrill={onEngineDrill} />
              </div>
            )}
          </div>
        ))}
      </div>
    </details>
  );
}

export function AgentTrail({ events, className, onEngineDrill }: AgentTrailProps) {
  const groups = useMemo(() => groupEvents(events), [events]);

  if (events.length === 0) {
    return (
      <div
        className={cn(
          'rounded border border-[var(--color-border-sub)] bg-[var(--color-bg-surface)] p-4 text-center text-sm text-[var(--color-text-dim)]',
          className
        )}
      >
        No deliberation events
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {groups.map((group) => (
        <EventGroupRow key={group.key} group={group} onEngineDrill={onEngineDrill} />
      ))}

      {/* Safeguard constants display (informational) */}
      <div className="mt-1 flex justify-end gap-3 text-[10px] text-[var(--color-text-ghost)]">
        <span>MAX_TOOL_ROUNDS: {MAX_TOOL_ROUNDS}</span>
        <span>MAX_REANALYSIS_ROUNDS: {MAX_REANALYSIS_ROUNDS}</span>
      </div>
    </div>
  );
}

export default AgentTrail;
